import React, { useEffect } from "react";
import { IconName, Notification } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { reportToGoogleAnalytics } from "../../../../google-analytics/state/googleAnalyticsMiddleware";
import cn from "classnames";
import "./Alert.less";

export type AlertKind = "info" | "warning" | "positive" | "negative" | "neutral";

export type AlertProps = {
    kind?: AlertKind;
    title?: string;
    className?: string;
    children?: React.ReactNode;
    type?: "tile" | "card";
    onClose?: () => void | Promise<void>;
    closeButtonText?: string;
    icon?: IconName;
    centered?: boolean;
};

type Props = AlertProps & {
    children: React.ReactElement | string;
};

/**
 * Traverse any props.children to get their combined text content.
 * This function is inspired by https://stackoverflow.com/a/63173682
 *
 */
const getTextContent = (elem: React.ReactElement | string): string => {
    if (!elem) {
        return "";
    }
    if (typeof elem === "string") {
        return elem;
    }
    if (elem.props.dangerouslySetInnerHTML) {
        return elem.props.dangerouslySetInnerHTML.__html;
    }
    const children = elem.props && elem.props.children;
    if (children instanceof Array) {
        return children.map(getTextContent).join("");
    }
    return getTextContent(children);
};

const mapKindToVariant = (kind?: AlertKind): "information" | "warning" | "error" | "success" | "gray" => {
    switch (kind) {
        case "warning":
            return "warning";
        case "negative":
            return "error";
        case "positive":
            return "success";
        case "neutral":
            return "gray";
        case "info":
        default:
            return "information";
    }
};

const Alert: React.FC<Props> = ({
    kind = "info",
    title,
    className,
    children,
    onClose,
    icon,
    closeButtonText,
    centered,
    ...props
}) => {
    useEffect(() => {
        let message;
        try {
            message = getTextContent(children);
            if (!_isEmpty(message)) {
                reportToGoogleAnalytics("WEBSHOP_WARNING", message);
            }
        } catch (error) {
            // oxlint-disable-next-line no-console
            console.warn("Couldn't send message to GA", { message, error });
        }
    }, []);

    const variant = mapKindToVariant(kind);

    const iconElement = title ? (
        <Notification.Heading as="h3">
            <Notification.Icon name={icon}>{title}</Notification.Icon>
        </Notification.Heading>
    ) : (
        <Notification.Icon name={icon} />
    );
    const textElement = children && (
        <Notification.Text className={centered ? "notification-alert__text" : undefined}>{children}</Notification.Text>
    );

    return (
        <Notification
            variant={variant}
            className={cn("notification-alert", className, {
                "notification-alert--centered": centered,
            })}
            {...props}
        >
            {onClose &&
                (closeButtonText ? (
                    <Notification.Dismiss>{closeButtonText}</Notification.Dismiss>
                ) : (
                    <Notification.Dismiss />
                ))}
            {centered ? (
                <div className="notification-alert__centeredContent">
                    {iconElement}
                    {textElement}
                </div>
            ) : (
                <>
                    {iconElement}
                    {textElement}
                </>
            )}
        </Notification>
    );
};

export default Alert;
